import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiGet } from '../../utils/api'
import { PageHeader } from '../shared/PageHeader'
import { Badge } from '../shared/Badge'
import { countryToFlag } from '../../utils/countryFlag'
import { safeInitial } from '../../utils/safeString'

interface ActivityChat {
  id: string | number
  status: string
  created_at: string
  closed_at?: string | null
  channel?: string
  visitor_name?: string | null
  company_name?: string | null
  company_logo_url?: string | null
  company_country?: string | null
  resolved?: boolean
}
interface AgentInfo {
  id: string; name: string; email: string; is_online?: boolean; avatar_url?: string | null
  open_chats?: number; total_chats_today?: number
  avg_response_time_seconds?: number; avg_resolution_time_seconds?: number; csat_score?: number
}

const fmtDur = (s?: number) => (!s || s <= 0 ? '—' : s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`)
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')

export const AgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const [agent, setAgent] = useState<AgentInfo | null>(null)
  const [chats, setChats] = useState<ActivityChat[]>([])
  const [stats, setStats] = useState<{ chats_handled: number; messages_sent: number; resolved: number; last_active_at: string | null } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!agentId) return
    let active = true
    ;(async () => {
      setLoading(true)
      try {
        const [statsResp, activity] = await Promise.all([
          apiGet<any>('/agents/stats').catch(() => ({ agents: [] })),
          apiGet<any>(`/agents/${agentId}/activity`).catch(() => ({ chats: [], stats: null })),
        ])
        if (!active) return
        const found = (statsResp.agents || []).find((a: any) => String(a.id) === String(agentId)) || null
        setAgent(found)
        setChats(activity.chats || [])
        setStats(activity.stats || null)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [agentId])

  const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="p-4 rounded-xl bg-surface border border-border">
      <p className="text-[10px] uppercase tracking-wide text-txt-tertiary">{label}</p>
      <p className="text-xl font-bold text-txt-primary mt-1">{value}</p>
    </div>
  )

  return (
    <div className="h-full overflow-y-auto">
      <PageHeader
        title={agent?.name || 'Agent'}
        subtitle={agent?.email}
        actions={
          <button onClick={() => navigate('/supervisor')} className="px-3.5 py-2 rounded-lg text-sm font-medium text-txt-secondary border border-border hover:bg-surface-hover transition-colors">
            ← Back to Team
          </button>
        }
      />

      <div className="p-6 max-w-5xl mx-auto">
        {/* Identity */}
        <div className="flex items-center gap-4 mb-6">
          {agent?.avatar_url ? (
            <img src={agent.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 flex items-center justify-center text-lg font-bold">{safeInitial(agent?.name)}</div>
          )}
          <div>
            <p className="text-lg font-semibold text-txt-primary">{agent?.name || '—'}</p>
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${agent?.is_online ? 'text-success' : 'text-txt-tertiary'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${agent?.is_online ? 'bg-success' : 'bg-txt-tertiary'}`} />
              {agent?.is_online ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <Stat label="Chats handled" value={String(stats?.chats_handled ?? 0)} />
          <Stat label="Open now" value={String(agent?.open_chats ?? 0)} />
          <Stat label="Today" value={String(agent?.total_chats_today ?? 0)} />
          <Stat label="Avg response" value={fmtDur(agent?.avg_response_time_seconds)} />
          <Stat label="Avg resolution" value={fmtDur(agent?.avg_resolution_time_seconds)} />
          <Stat label="CSAT" value={agent?.csat_score ? `${agent.csat_score.toFixed(1)}/5` : '—'} />
        </div>

        {/* Chat history */}
        <h2 className="text-base font-bold text-txt-primary mb-3">Chat history <span className="text-sm font-normal text-txt-tertiary">({chats.length})</span></h2>
        {loading ? (
          <p className="text-sm text-txt-tertiary">Loading…</p>
        ) : chats.length === 0 ? (
          <p className="text-sm text-txt-tertiary">No chats handled yet.</p>
        ) : (
          <div className="rounded-xl bg-surface border border-border divide-y divide-border">
            {chats.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/queue/${c.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors text-left"
              >
                {/* company logo + flag */}
                <div className="relative shrink-0">
                  {c.company_logo_url ? (
                    <img src={c.company_logo_url} alt="" className="w-9 h-9 rounded-full object-cover bg-surface-secondary border border-border" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-600 flex items-center justify-center text-sm font-semibold">{safeInitial(c.company_name || c.visitor_name || '?')}</div>
                  )}
                  {countryToFlag(c.company_country) && (
                    <span className="absolute -bottom-1 -right-1 text-xs leading-none" title={c.company_country || ''}>{countryToFlag(c.company_country)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-txt-primary truncate">{c.visitor_name || c.company_name || `Chat #${c.id}`}</span>
                    <Badge variant="status" value={c.status} />
                    {c.resolved && <span className="text-[10px] font-medium px-1.5 py-px rounded bg-green-100 text-green-700">Resolved</span>}
                  </div>
                  <p className="text-xs text-txt-tertiary truncate">{c.company_name || 'Unknown company'} · {fmtDate(c.created_at)}</p>
                </div>
                <svg className="w-4 h-4 text-txt-tertiary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
